import { AnalyticsService } from 'core/analytics/interfaces';
import { notAvailable } from 'core/common/constants';
import { CustomAppEvents } from 'core/common/entities';
import { normalizeError } from 'core/common/errors';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { Logger } from 'core/common/services/logger/interfaces';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentState } from 'core/cookie-consent/entities';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { HeatmapRecordingService } from './HeatmapRecording';

export type HeatmapRecordingIdentifyUserOptions = {
  customId: string;
  customSessionId?: string;
  customPageId?: string;
  friendlyName?: string;
};

export class ClarityHeatmapRecordingClientService implements HeatmapRecordingService {
  private readonly logger: Logger;
  private readonly analyticsService: AnalyticsService;
  private readonly cookieConsentService: CookieConsentService;
  private readonly errorMonitoringService: ErrorMonitoring;
  private initialized = false;

  constructor(
    logger: Logger,
    analyticsService: AnalyticsService,
    cookieConsentService: CookieConsentService,
    errorMonitoringService: ErrorMonitoring,
  ) {
    this.logger = logger;
    this.analyticsService = analyticsService;
    this.cookieConsentService = cookieConsentService;
    this.errorMonitoringService = errorMonitoringService;

    const onConsentChange = (consentState: CookieConsentState) => {
      if (consentState.PERFORMANCE) {
        this.giveCookieConsent();
      }
    };
    this.cookieConsentService.addConsentChangeListener(onConsentChange);
  }

  private get clarity() {
    if (!checkIsClientSide()) {
      return;
    }

    return window.clarity;
  }

  private track(
    command: 'identify',
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string,
  ): Promise<{ userId: string; session: string; page: string; userHint: string } | undefined>;
  private track(command: 'consent', given?: boolean): void;
  private track(command: 'event', value: string): void;
  private track(command: 'set', key: string, value: string | [string, string]): void;
  private track(command: 'upgrade', reason: string): void;

  private track(command: string, ...args: Array<unknown>): unknown {
    if (!this.clarity) {
      return;
    }

    return this.clarity(command, ...args);
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    if (this.clarity) {
      this.startRecording();
      this.initialized = true;
      return;
    }

    document.addEventListener(
      CustomAppEvents.CLARITY_LOADED,
      () => {
        this.startRecording();
        this.initialized = true;
      },
      { once: true },
    );
  }

  startRecording() {
    const deviceId = this.analyticsService.getDeviceId();

    if (this.cookieConsentService.isInitialized()) {
      this.setInitialConsentState();
    } else {
      this.deferSetInitialConsentState();
    }

    this.identifyUser({ customId: deviceId });

    if (deviceId) this.addCustomTag('device id', deviceId);
  }

  triggerCustomEvent(value: string) {
    this.track('event', value);
  }

  addCustomTag(key: string, value: string | [string, string]) {
    this.track('set', key, value);
  }

  identify(email: string) {
    const deviceId = this.analyticsService.getDeviceId();

    this.identifyUser({ friendlyName: email, customId: deviceId });
    this.addCustomTag('email', email);
  }

  markImportantSession(reason: string) {
    this.track('upgrade', reason);
  }

  private async identifyUser(options: HeatmapRecordingIdentifyUserOptions) {
    try {
      const userData = await this.track(
        'identify',
        options.customId,
        options.customSessionId,
        options.customPageId,
        options.friendlyName,
      );

      if (userData && userData.userId && userData.userHint) {
        this.analyticsService.updateUserProperties({
          clarityHashId: userData.userId,
          clarityUserHint: userData.userHint,
        });
      }
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error('Failed to identify user for clarity', {
        error: error.message,
      });
      this.errorMonitoringService.captureError(error, {
        tags: {
          get_params: window?.location?.search || notAvailable,
          error_message: error.message,
          error_reason: 'identify_user_clarity',
        },
      });
    }
  }

  private deferSetInitialConsentState(): void {
    document.addEventListener(
      CustomAppEvents.ONE_TRUST_LOADED,
      () => {
        this.setInitialConsentState();
      },
      { once: true },
    );
  }

  private setInitialConsentState(): void {
    if (this.cookieConsentService.isPerformanceConsentGiven()) {
      this.giveCookieConsent();
    }
  }

  giveCookieConsent() {
    this.track('consent');
  }

  revokeCookieConsent() {
    this.track('consent', false);
  }
}
